import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchCounts,
  updateCount,
  createCount,
  deleteCount,
  fetchCount,
} from "../../api/inventory/count";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  count: {},
  itemsCount: 0,
  page: 1,
  totalPages: 1,
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteCountAsync = createAsyncThunk(
  "inventory_counts/deleteCountAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteCount(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "delete_inventory_count");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_inventory_count");
    }
  }
);

export const createCountAsync = createAsyncThunk(
  "inventory_counts/createCountAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createCount(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_inventory_count");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_inventory_count");
    }
  }
);

export const updateCountAsync = createAsyncThunk(
  "inventory_counts/updateCountAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateCount(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_inventory_count");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_inventory_count");
    }
  }
);

export const getCountsAsync = createAsyncThunk(
  "inventory_counts/getCountsAsync",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const response = await fetchCounts(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "list_inventory_counts");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_inventory_counts");
    }
  }
);

export const getCountAsync = createAsyncThunk(
  "inventory_counts/getCountAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchCount(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_inventory_count");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_inventory_count");
    }
  }
);

const InventoryCountSlice = createSlice({
  name: "inventory_counts",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCountsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getCountsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        if (Array.isArray(action.payload)) {
          state.data = action.payload;
          state.itemsCount = action.payload.length;
        } else if (action.payload?.data) {
          state.data = action.payload.data;
          state.itemsCount = action.payload.count || action.payload.data.length;
          state.page = action.payload.page || 1;
          state.totalPages = action.payload.total_pages || 1;
        } else {
          state.data = [];
          state.itemsCount = 0;
        }
      })
      .addCase(getCountsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente inventartellingene.");
      })
      .addCase(updateCountAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateCountAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.count = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateCountAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke oppdatere inventartellingen."
        );
      })
      .addCase(createCountAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createCountAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createCountAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke opprette inventartellingen."
        );
      })
      .addCase(deleteCountAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteCountAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(deleteCountAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke slette inventartellingen."
        );
      })
      .addCase(getCountAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getCountAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.count = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getCountAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente inventartellingen."
        );
      });
  },
});

export const { reducer } = InventoryCountSlice;

export default InventoryCountSlice;

export const selectInventoryCountById = (state, itemId) =>
  state.inventory_counts.data.find((item) => item.id == itemId);
